import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UniversitySelectionModal = ({ open, onClose }) => {
  const [universities, setUniversities] = useState([]);
  const [formData, setFormData] = useState({
    university_name: '',
    course_title: '',
    country: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchUniversities();
    }
  }, [open]);

  const fetchUniversities = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/universities/shortlist`);
      setUniversities(response.data);
    } catch (error) {
      console.error('Failed to fetch universities:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (universities.length >= 5) {
      toast.error('Maximum 5 universities allowed');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/universities/shortlist`, formData);
      toast.success('University added to shortlist!');
      setFormData({ university_name: '', course_title: '', country: '' });
      fetchUniversities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add university');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (universityId) => {
    try {
      await axios.delete(`${API_URL}/api/universities/${universityId}`);
      toast.success('University removed from shortlist');
      fetchUniversities();
    } catch (error) {
      toast.error('Failed to remove university');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading font-semibold text-2xl">University & Course Selection</DialogTitle>
          <p className="text-muted-foreground">Shortlist up to 5 universities</p>
        </DialogHeader>

        <div className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4 p-4 rounded-xl bg-muted">
            <div className="space-y-2">
              <Label htmlFor="university">University Name</Label>
              <Input
                id="university"
                value={formData.university_name}
                onChange={(e) => setFormData({ ...formData, university_name: e.target.value })}
                placeholder="e.g., University of Oxford"
                required
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="course">Course Title</Label>
              <Input
                id="course"
                value={formData.course_title}
                onChange={(e) => setFormData({ ...formData, course_title: e.target.value })}
                placeholder="e.g., Master of Science in Computer Science"
                required
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Input
                id="country"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                placeholder="e.g., United Kingdom"
                required
                className="rounded-xl"
              />
            </div>

            <Button
              type="submit"
              disabled={loading || universities.length >= 5}
              className="w-full rounded-full bg-primary hover:bg-primary/90"
            >
              {loading ? 'Adding...' : `Add to Shortlist (${universities.length}/5)`}
            </Button>
          </form>

          {universities.length > 0 && (
            <div>
              <h3 className="font-heading font-semibold text-lg mb-3">Your Shortlisted Universities</h3>
              <div className="space-y-3">
                {universities.map((uni, index) => (
                  <div key={uni.id} className="p-4 rounded-xl border bg-card">
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-primary">{index + 1}.</span>
                          <h4 className="font-heading font-semibold text-foreground">{uni.university_name}</h4>
                        </div>
                        <p className="text-sm text-foreground mb-1">{uni.course_title}</p>
                        <p className="text-xs text-muted-foreground">{uni.country}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(uni.id)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UniversitySelectionModal;